import { useState, type ReactNode } from 'react';
import { Mail, Phone, type LucideIcon } from 'lucide-react';
import { contactDisplayName, toTelHref, type PaymentContact } from '../utils/paymentDisplay';

/**
 * Briques visuelles partagées par la page de paiement publique.
 * Les couleurs de marque passent par les variables CSS posées à la racine
 * de la page (`themeCssVars`) : --pay-accent-soft, --pay-ink, etc.
 */

export function IconBadge({ icon: Icon, size = 'md' }: { icon: LucideIcon; size?: 'sm' | 'md' }) {
  const box = size === 'sm' ? 'tw-h-8 tw-w-8 tw-rounded-lg' : 'tw-h-10 tw-w-10 tw-rounded-xl';
  const glyph = size === 'sm' ? 'tw-h-4 tw-w-4' : 'tw-h-5 tw-w-5';
  return (
    <span
      aria-hidden="true"
      className={`tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-bg-[color:var(--pay-accent-soft)] tw-text-[color:var(--pay-ink)] ${box}`}
    >
      <Icon className={glyph} />
    </span>
  );
}

/** Logo dans un cartouche blanc ; masqué si l'image ne se charge pas. */
export function BrandLogo({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <span className="tw-inline-flex tw-h-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-white tw-px-3 tw-shadow-sm tw-ring-1 tw-ring-black/5 sm:tw-h-12">
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        className="tw-h-3/4 tw-w-auto tw-max-w-[150px] tw-object-contain"
      />
    </span>
  );
}

/** Ligne libellé / valeur du récapitulatif. */
export function SummaryRow({ label, children, emphasis = false }: { label: string; children: ReactNode; emphasis?: boolean }) {
  return (
    <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-4 tw-py-1.5">
      <span className={emphasis ? 'tw-text-sm tw-font-semibold tw-text-gray-900' : 'tw-text-sm tw-text-gray-600'}>{label}</span>
      <span
        className={
          emphasis
            ? 'tw-text-right tw-text-base tw-font-bold tw-text-gray-900'
            : 'tw-text-right tw-text-sm tw-font-medium tw-text-gray-900'
        }
      >
        {children}
      </span>
    </div>
  );
}

const CONTACT_LINK_CLASS =
  'tw-inline-flex tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-[color:var(--pay-ink)] tw-no-underline tw-transition-colors hover:tw-bg-slate-50';

/** « Une question ? Contactez … » avec liens téléphone / e-mail quand ils sont connus. */
export function ContactLine({
  contact,
  className = '',
  align = 'start',
}: {
  contact: PaymentContact;
  className?: string;
  align?: 'start' | 'center';
}) {
  const centered = align === 'center';
  return (
    <div className={`${centered ? 'tw-text-center' : ''} ${className}`}>
      <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-gray-900">
        Une question ? Contactez {contactDisplayName(contact)}
      </p>
      {(contact.phone || contact.email) && (
        <div className={`tw-mt-3 tw-flex tw-flex-wrap tw-gap-2 ${centered ? 'tw-justify-center' : ''}`}>
          {contact.phone && (
            <a href={toTelHref(contact.phone)} className={CONTACT_LINK_CLASS}>
              <Phone className="tw-h-4 tw-w-4 tw-flex-shrink-0" aria-hidden="true" />
              {contact.phone}
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}`} className={CONTACT_LINK_CLASS}>
              <Mail className="tw-h-4 tw-w-4 tw-flex-shrink-0" aria-hidden="true" />
              <span className="tw-truncate">{contact.email}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}
